import type { Stop } from '@getcircuit/schema'

import type { ErrorResponse, SuccessResponse, ClientContext } from '../../types'
import type {
  AddressSearchResultItem,
  GeocodeInterface,
  LatLng,
  StopData,
} from './types'
import { mapAddressSearchResultItemToStop, createStop } from './utils'

export type MatchObject = {
  index: [number, number]
  key: string
  value: string
}

type ExistingStop = Stop & {
  matches?: MatchObject[]
}

type SearchAddressResponse =
  | SuccessResponse<{
      existingStops: ExistingStop[]
      suggestions: AddressSearchResultItem[]
    }>
  | ErrorResponse

export function searchAddress(
  context: ClientContext,
  {
    current,
    stops,
    query,
    searchToken,
    placeId,
    searchMode,
    trimMatch,
  }: {
    current?: LatLng
    stops: Stop[]
    query?: string
    searchToken?: string
    placeId?: string
    trimMatch?: boolean
    searchMode: 'all' | 'existing_only' | 'suggested_only'
  },
): Promise<SearchAddressResponse> {
  const locationStops = stops.filter(
    (stop) => stop?.latitude && stop?.longitude,
  )

  return context
    .request(`/addresses/search`, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      json: {
        query,
        trimMatch,
        searchToken,
        placeId,
        searchMode,
        locations: {
          current,
          stops: locationStops,
        },
      },
    })
    .catch((e) => {
      console.error(e)

      return undefined
    })
}

export async function geocodeStop(
  context: ClientContext,
  { stops, query, searchToken, placeId }: GeocodeInterface,
): Promise<Stop | undefined> {
  const locationStops = [...stops.values()]
    .filter((stop) => stop?.latitude && stop?.longitude)
    .map((stop: Stop | null) => createStop(stop as StopData))

  const result = await context
    .request<AddressSearchResultItem>('/addresses/geocode', {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      json: {
        query,
        searchToken,
        placeId,
        locations: { stops: locationStops },
      },
    })
    .catch((e) => {
      console.error(e)

      return undefined
    })

  if (result == null) {
    return undefined
  }

  return mapAddressSearchResultItemToStop(result)
}
